import type { HomeDay } from '../../api/client.ts';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';

const W = 600;
const H = 160;
const PAD = 24;

export interface ChartSeries {
  label: string;
  value: (day: HomeDay) => number;
  dashed?: boolean;
}

/** Daily lines on a shared axis; each point says its day and value on hover and to screen readers via the summary. */
export function HomeChart({ title, days, series }: { title: string; days: HomeDay[]; series: ChartSeries[] }) {
  const max = Math.max(1, ...days.flatMap((d) => series.map((s) => s.value(d))));
  const x = (i: number) => PAD + (days.length > 1 ? i / (days.length - 1) : 0.5) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - (v / max) * (H - 2 * PAD);
  const summary = series.map((s) => `${s.label} from ${formatCount(Math.round(s.value(days[0]!)))} to ${formatCount(Math.round(s.value(days.at(-1)!)))}`).join('; ');
  return (
    <figure className="home-chart">
      <figcaption>
        {title}
        {series.length > 1 && (
          <span className="home-chart__legend">
            {series.map((s) => (
              <span key={s.label} className={s.dashed ? 'home-chart__key home-chart__key--dashed' : 'home-chart__key'}>
                {s.label}
              </span>
            ))}
          </span>
        )}
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}, ${days[0]!.day} to ${days.at(-1)!.day}: ${summary}`}>
        {series.map((s) => (
          <g key={s.label}>
            <path d={days.map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(s.value(d)).toFixed(1)}`).join(' ')} fill="none" stroke="currentColor" strokeWidth={2} strokeDasharray={s.dashed ? '6 4' : undefined} />
            {days.map((d, i) => (
              <circle key={d.day} cx={x(i)} cy={y(s.value(d))} r={2.5} fill="currentColor">
                <title>{`${d.day}: ${s.label} ${formatCount(Math.round(s.value(d) * 100) / 100)}`}</title>
              </circle>
            ))}
          </g>
        ))}
        <text x={PAD} y={H - 6} fontSize={11}>
          {days[0]!.day}
        </text>
        <text x={W - PAD} y={H - 6} fontSize={11} textAnchor="end">
          {days.at(-1)!.day}
        </text>
      </svg>
    </figure>
  );
}
